import React, { useRef } from 'react'
import { makeStyles } from '@material-ui/core'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '@paintscout/ui'
import { IconButton } from '@paintscout/ui'
import classNames from 'classnames'
import DragIndicatorIcon from '@material-ui/icons/DragIndicator'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import { useDrag, useDragLayer, useDrop } from 'react-dnd'
import type { User } from '@paintscout/api'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      backgroundColor: 'rgba(241, 245, 249, 0.5)', // bg-slate-100/50
      height: '44px', // h-[44px]
      transition: 'all 0.2s', // transition-all
      display: 'flex', // flex
      alignItems: 'center', // items-center
      position: 'relative', // relative
      '&:hover $dragHandle': {
        width: theme.spacing(3),
        opacity: 1
      },
      '&:hover $expandButton': {
        paddingLeft: theme.spacing(0.5),
        paddingRight: theme.spacing(0.5)
      },
      '&:hover $hiddenExpand': {
        width: theme.spacing(3)
      },
      '&:hover $avatar': {
        marginLeft: 0
      },
      '&:hover $letterAvatar': {
        marginLeft: 0
      },
      '&:hover &shift': {
        marginLeft: 0
      }
    },
    smallWidth: {
      paddingLeft: theme.spacing(0.5),
      marginRight: theme.spacing(-2),
      gap: theme.spacing(1)
    },
    bigWidth: {
      paddingRight: theme.spacing(3),
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
      gap: theme.spacing(2)
    },
    dragHandle: {
      cursor: 'move',
      padding: theme.spacing(0.5),
      borderRadius: theme.shape.borderRadius,
      transition: 'all 0.2s',
      flexShrink: 0,
      overflow: 'hidden',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: theme.spacing(0.5),
      opacity: 0,
      width: 0,
      '&:hover': {
        backgroundColor: 'rgba(226, 232, 240, 0.6)' // slate-200/60
      }
    },
    dragging: {
      opacity: 0.5
    },
    over: {
      cursor: 'pointer'
    },
    dragIcon: {
      width: theme.spacing(2), // 3.5 * 8px (default spacing unit)
      height: theme.spacing(2),
      color: '#94a3b8'
    },
    arrowIcon: {
      width: theme.spacing(3.5),
      height: theme.spacing(3.5),
      color: '#64748b',
      zIndex: 0
    },
    expandButton: {
      // No direct equivalent for md:p-0.5 in JSS, handle via responsive styles if needed
      borderRadius: theme.shape.borderRadius,
      transition: 'background-color 0.2s',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      flexShrink: 0,
      '&:hover': {
        backgroundColor: 'rgba(226, 232, 240, 0.6)' // slate-200/60
      }
    },
    visibleExpand: {
      width: 18
    },
    hiddenExpand: {
      overflow: 'hidden',
      width: 0,
      transition: 'width 0.2s'
    },
    avatar: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      objectFit: 'cover',
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      flexShrink: 0,
      transition: 'all 0.2s',
      zIndex: 10,
      cursor: 'pointer'
    },
    letterAvatar: {
      width: 32,
      height: 32,
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#fff',
      boxShadow: '0 1px 2px 0 rgba(0,0,0,0.05)',
      flexShrink: 0,
      transition: 'all 0.2s',
      zIndex: 10
    },
    shift: {
      marginLeft: '-28px'
    },
    leadDetails: {
      flex: 1,
      minWidth: 0,
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center'
    },
    leadDetailsText: {
      fontSize: '0.875rem', // text-sm
      color: '#1e293b',
      fontWeight: 500, // font-medium
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    revenueDetailsText: {
      fontSize: '10px',
      color: '#64748b',
      whiteSpace: 'nowrap'
    },
    customDragLayer: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing(1),
      padding: theme.spacing(1),
      backgroundColor: 'rgba(241, 245, 249, 0.9)', // bg-slate-100/90
      borderRadius: theme.shape.borderRadius,
      boxShadow: theme.shadows[4]
    }
  }),
  { name: 'AgendaHeaderLeftColumn' }
)

function CustomDragLayer() {
  const classes = useStyles()
  const { isDragging, item, currentOffset } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    item: monitor.getItem(),
    currentOffset: monitor.getClientOffset()
  }))
  if (!isDragging || !currentOffset || !item?.projectManagerId || !item?.projectManager) {
    return null
  }

  const firstInitial =
    item.projectManager?.user_metadata?.firstName?.[0] ?? item.projectManager?.name?.[0]?.toUpperCase() ?? '?'
  const lastInitial = item.projectManager?.user_metadata?.lastName?.[0] ?? ''

  return (
    <div
      style={{
        position: 'fixed',
        pointerEvents: 'none',
        zIndex: 100,
        left: currentOffset.x,
        top: currentOffset.y,
        transform: 'translate(-50%, -50%)'
      }}
    >
      <div className={classes.customDragLayer}>
        {item?.projectManager?.user_metadata?.customAvatar?.value ? (
          <img
            src={item?.projectManager?.user_metadata?.customAvatar?.value}
            alt={item?.projectManager?.name}
            className={classNames({ [classes.avatar]: true })}
          />
        ) : (
          <div
            className={classNames({ [classes.letterAvatar]: true })}
            style={{ backgroundColor: item?.projectManager?.user_metadata?.color ?? 'blue' }}
          >
            <span className="text-xs">{`${firstInitial}${lastInitial}`}</span>
          </div>
        )}
        <div className={classes.leadDetailsText}>{item?.projectManager?.name}</div>
      </div>
    </div>
  )
}

export interface AgendaHeaderLeftColumnProps {
  classes?: StyleClasses<typeof useStyles>
  index: number
  projectManager: User
  totalRevenue: number
  isExpanded: boolean
  showControls?: boolean
  onToggleExpand: () => void
  onMoveLead: (draggedLeadId: string, targetLeadId: string) => void
  handleAvatarClick: () => void
  leftColumnWidth: number
}

function AgendaHeaderLeftColumn(props: AgendaHeaderLeftColumnProps) {
  const classes = useStyles(props)
  const { projectManager, totalRevenue, isExpanded, onToggleExpand, leftColumnWidth, onMoveLead, handleAvatarClick } =
    props
  const showControls = props.showControls ?? true
  const ref = useRef<HTMLDivElement>(null)
  const dragHandleRef = useRef<HTMLDivElement>(null)
  const firstInitial = projectManager?.user_metadata?.firstName?.[0] ?? projectManager?.name?.[0]?.toUpperCase() ?? '?'
  const lastInitial = projectManager?.user_metadata?.lastName?.[0] ?? ''

  // Drag and drop for lead reordering
  const [{ isDragging }, drag, preview] = useDrag({
    type: 'LEAD',
    item: { projectManagerId: projectManager.user_id, projectManager },
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOver }, drop] = useDrop({
    accept: 'LEAD',
    drop: (item: { projectManagerId: string }) => {
      if (item.projectManagerId !== projectManager.user_id) {
        onMoveLead(item.projectManagerId, projectManager.user_id)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver()
    })
  })

  preview(drop(ref))
  drag(dragHandleRef)

  return (
    <>
      <CustomDragLayer />
      <div
        ref={ref}
        className={classNames({
          [classes.root]: true,
          [classes.smallWidth]: leftColumnWidth <= 100,
          [classes.bigWidth]: leftColumnWidth > 100,
          [classes.dragging]: isDragging,
          [classes.over]: isOver
        })}
        onMouseEnter={() => {
          if (leftColumnWidth <= 100) {
            handleAvatarClick()
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '10px' }}>
          {/* Drag Handle - hidden on mobile/narrow widths and by default on desktop, expands on hover */}
          {showControls && leftColumnWidth > 100 && (
            <div ref={dragHandleRef} className={classes.dragHandle} title="Drag to reorder">
              <DragIndicatorIcon className={classes.dragIcon} />
            </div>
          )}

          {/* Expand/Collapse Button - always visible on mobile (narrow), hidden by default on desktop */}
          {showControls && (
            <IconButton
              onClick={onToggleExpand}
              aria-label="Toggle project manager row"
              className={classNames({
                [classes.expandButton]: true,
                [classes.visibleExpand]: leftColumnWidth <= 100,
                [classes.hiddenExpand]: leftColumnWidth > 100
              })}
            >
              {isExpanded ? (
                <KeyboardArrowDownIcon className={classes.arrowIcon} />
              ) : (
                <ChevronRightIcon className={classes.arrowIcon} />
              )}
            </IconButton>
          )}
        </div>
        {/* Lead Avatar - shifts left when controls are hidden on desktop, stays in place on mobile */}
        {projectManager.user_metadata?.customAvatar?.value ? (
          <img
            src={projectManager.user_metadata.customAvatar.value}
            alt={projectManager.name}
            className={classNames({ [classes.avatar]: true, [classes.shift]: leftColumnWidth > 100 && showControls })}
            onClick={handleAvatarClick}
          />
        ) : (
          <div
            className={classNames({
              [classes.letterAvatar]: true,
              [classes.shift]: leftColumnWidth > 100 && showControls
            })}
            style={{ backgroundColor: projectManager?.user_metadata?.color ?? 'blue' }}
            onClick={handleAvatarClick}
          >
            <span className="text-xs">{`${firstInitial}${lastInitial}`}</span>
          </div>
        )}

        {/* Lead Details - only show when column is wide enough */}
        {leftColumnWidth > 100 && (
          <div className={classes.leadDetails}>
            <div className={classes.leadDetailsText}>{projectManager.name}</div>
            <div className={classes.revenueDetailsText}>${totalRevenue.toLocaleString()}</div>
          </div>
        )}
      </div>
    </>
  )
}

export default AgendaHeaderLeftColumn
